import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Eye, Check, X, Download, Clock, Shield, AlertCircle } from 'lucide-react';
import apiService from '../../services/api';
import { toast } from 'react-hot-toast';
import '../../styles/responsive-admin.css';

const DocumentsManager = ({ user }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verificationData, setVerificationData] = useState({
    verification_status: '',
    rejection_reason: '',
  });

  useEffect(() => {
    fetchPendingDocuments();
  }, []);

  const fetchPendingDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPendingDocuments();
      setDocuments(response || []);
    } catch (error) {
      toast.error('Failed to fetch documents');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDocument = async () => {
    if (!selectedDocument || !verificationData.verification_status) {
      toast.error('Please select verification status');
      return;
    }

    try {
      await apiService.verifyDocument(selectedDocument.id, verificationData);
      toast.success('Document verification updated');
      setShowVerifyModal(false);
      setSelectedDocument(null);
      setVerificationData({ verification_status: '', rejection_reason: '' });
      fetchPendingDocuments();
    } catch (error) {
      toast.error('Failed to update verification');
      console.error('Error:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#ff9800';
      case 'verified':
        return '#4caf50';
      case 'rejected':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <div>Loading documents...</div>
      </div>
    );
  }

  return (
    <div className='responsive-main'>
      <div className='responsive-header'>
        <div>
          <h1>Documents Manager</h1>
          <p
            style={{ margin: '8px 0 0 0', color: 'var(--gray-600)', fontSize: 'var(--text-base)' }}
          >
            Review and verify uploaded documents
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            color: 'var(--primary)',
          }}
        >
          <Shield size={20} />
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: '500' }}>Secure Review</span>
        </div>
      </div>

      {documents.length === 0 ? (
        <div
          className='responsive-card'
          style={{
            textAlign: 'center',
            padding: 'var(--space-16) var(--space-8)',
          }}
        >
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: 'var(--radius-full)',
              background: 'linear-gradient(135deg, var(--gray-100) 0%, var(--gray-200) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-6) auto',
            }}
          >
            <FileText size={40} style={{ color: 'var(--gray-400)' }} />
          </div>
          <h3
            style={{
              margin: '0 0 var(--space-2) 0',
              color: 'var(--gray-700)',
              fontSize: 'var(--text-xl)',
              fontWeight: '600',
            }}
          >
            No pending documents
          </h3>
          <p style={{ margin: 0, color: 'var(--gray-500)', fontSize: 'var(--text-base)' }}>
            All documents have been reviewed
          </p>
        </div>
      ) : (
        <div className='responsive-grid' style={{ gap: 'var(--space-6)' }}>
          {documents.map((doc) => (
            <motion.div
              key={doc.id}
              className='responsive-card'
              whileHover={{ y: -4, scale: 1.02 }}
              style={{ position: 'relative', overflow: 'hidden' }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${getStatusColor(doc.verification_status)} 0%, ${getStatusColor(doc.verification_status)}80 100%)`,
                }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 'var(--space-6)',
                  paddingTop: 'var(--space-2)',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      marginBottom: 'var(--space-3)',
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: 'var(--radius-lg)',
                        background:
                          'linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FileText size={20} color='white' />
                    </div>
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: 'var(--text-lg)',
                          fontWeight: '600',
                          color: 'var(--gray-900)',
                        }}
                      >
                        {doc.document_type}
                      </h3>
                      <p
                        style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--gray-500)' }}
                      >
                        {doc.original_name}
                      </p>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: 'var(--space-2)',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--gray-600)',
                      marginBottom: 'var(--space-2)',
                    }}
                  >
                    <span
                      style={{
                        background: 'var(--gray-100)',
                        padding: 'var(--space-1) var(--space-2)',
                        borderRadius: 'var(--radius)',
                        fontWeight: '500',
                      }}
                    >
                      Entity: {doc.entity_type}
                    </span>
                    <span
                      style={{
                        background: 'var(--gray-100)',
                        padding: 'var(--space-1) var(--space-2)',
                        borderRadius: 'var(--radius)',
                        fontWeight: '500',
                      }}
                    >
                      ID: {doc.entity_id}
                    </span>
                    <span
                      style={{
                        background: 'var(--gray-100)',
                        padding: 'var(--space-1) var(--space-2)',
                        borderRadius: 'var(--radius)',
                        fontWeight: '500',
                      }}
                    >
                      Size: {formatFileSize(doc.file_size)}
                    </span>
                  </div>
                  {doc.UploadedBy && (
                    <div
                      style={{
                        fontSize: 'var(--text-sm)',
                        color: 'var(--gray-600)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                      }}
                    >
                      <div
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: 'var(--success)',
                        }}
                      />
                      Uploaded by: <strong>{doc.UploadedBy.name}</strong> ({doc.UploadedBy.email})
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span
                    className={`responsive-badge ${doc.verification_status === 'verified' ? 'responsive-badge-success' : doc.verification_status === 'rejected' ? 'responsive-badge-danger' : 'responsive-badge-warning'}`}
                  >
                    {doc.verification_status === 'verified' ? (
                      <Check size={12} />
                    ) : doc.verification_status === 'rejected' ? (
                      <X size={12} />
                    ) : (
                      <Clock size={12} />
                    )}
                    {doc.verification_status || 'pending'}
                  </span>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  justifyContent: 'flex-end',
                  paddingTop: 'var(--space-4)',
                  borderTop: '1px solid var(--gray-200)',
                }}
              >
                <button
                  onClick={() => {
                    setSelectedDocument(doc);
                    setShowVerifyModal(true);
                  }}
                  className='responsive-btn responsive-btn-primary'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                  }}
                >
                  <Eye size={16} />
                  Review Document
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Verification Modal */}
      {showVerifyModal && selectedDocument && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 'var(--z-modal)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <motion.div
            className='responsive-card'
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            style={{
              width: '600px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                marginBottom: 'var(--space-6)',
                paddingBottom: 'var(--space-4)',
                borderBottom: '1px solid var(--gray-200)',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--radius-lg)',
                  background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Shield size={24} color='white' />
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-xl)',
                    fontWeight: '600',
                    color: 'var(--gray-900)',
                  }}
                >
                  Document Verification
                </h3>
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--gray-600)' }}>
                  Review and verify document authenticity
                </p>
              </div>
            </div>

            <div
              style={{
                background: 'var(--gray-50)',
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-lg)',
                marginBottom: 'var(--space-6)',
                border: '1px solid var(--gray-200)',
              }}
            >
              <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
                <div>
                  <strong style={{ color: 'var(--gray-700)' }}>Document:</strong>{' '}
                  <span style={{ color: 'var(--gray-900)' }}>{selectedDocument.document_type}</span>
                </div>
                <div>
                  <strong style={{ color: 'var(--gray-700)' }}>File:</strong>{' '}
                  <span style={{ color: 'var(--gray-900)' }}>{selectedDocument.original_name}</span>
                </div>
                <div>
                  <strong style={{ color: 'var(--gray-700)' }}>Entity:</strong>{' '}
                  <span style={{ color: 'var(--gray-900)' }}>
                    {selectedDocument.entity_type} #{selectedDocument.entity_id}
                  </span>
                </div>
              </div>
            </div>

            <div className='responsive-form-group'>
              <label className='responsive-form-label'>Verification Status *</label>
              <select
                value={verificationData.verification_status}
                onChange={(e) =>
                  setVerificationData({
                    ...verificationData,
                    verification_status: e.target.value,
                  })
                }
                className='responsive-form-input responsive-form-select'
              >
                <option value=''>Select Status</option>
                <option value='verified'>✓ Verified</option>
                <option value='rejected'>✗ Rejected</option>
              </select>
            </div>

            {verificationData.verification_status === 'rejected' && (
              <motion.div
                className='responsive-form-group'
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                style={{
                  background: 'rgba(239, 68, 68, 0.05)',
                  padding: 'var(--space-4)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                <label
                  className='responsive-form-label'
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                >
                  <AlertCircle size={16} color='var(--danger)' />
                  Rejection Reason
                </label>
                <textarea
                  value={verificationData.rejection_reason}
                  onChange={(e) =>
                    setVerificationData({
                      ...verificationData,
                      rejection_reason: e.target.value,
                    })
                  }
                  placeholder='Please provide a detailed reason for rejection...'
                  className='responsive-form-input responsive-form-textarea'
                />
              </motion.div>
            )}

            <div
              style={{
                display: 'flex',
                gap: 'var(--space-3)',
                justifyContent: 'flex-end',
                paddingTop: 'var(--space-6)',
                borderTop: '1px solid var(--gray-200)',
              }}
            >
              <button
                onClick={() => {
                  setShowVerifyModal(false);
                  setSelectedDocument(null);
                  setVerificationData({ verification_status: '', rejection_reason: '' });
                }}
                className='responsive-btn responsive-btn-secondary'
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyDocument}
                className={`responsive-btn ${verificationData.verification_status === 'verified' ? 'btn-success' : 'btn-danger'}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                }}
              >
                {verificationData.verification_status === 'verified' ? (
                  <>
                    <Check size={16} />
                    Verify Document
                  </>
                ) : (
                  <>
                    <X size={16} />
                    Reject Document
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DocumentsManager;
